import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import styles from './Omnibox.module.css';

/**
 * Okno omniboksa — **własne, nie Radix Dialog**.
 *
 * ⭐ Uzasadnienie jest konkretne, nie ideologiczne: w tym oknie stoi **dokładnie jeden
 * element łapiący ognisko** — pole tekstowe. Pozycje listy mają `role="option"` bez
 * `tabIndex`, bo wzorzec combobox wymaga, żeby ognisko z pola nie wychodziło. Cała
 * maszyneria pułapki ogniska, dla której bierze się `Dialog`, rozwiązuje tu problem,
 * którego nie mamy.
 *
 * Zostaje pięć rzeczy, które faktycznie są potrzebne, i wszystkie są poniżej: portal,
 * przyciemnienie tła, Escape, blokada przewijania i **przywrócenie ogniska** tam, skąd
 * okno otwarto.
 *
 * ⚠ Gdyby kiedyś w oknie pojawiły się prawdziwe kontrolki (przyciski, pola), ta decyzja
 * wymaga rewizji — wtedy pułapka ogniska przestaje być trywialna.
 */
interface OmniboxDialogProps {
  open: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
}

export function OmniboxDialog({ open, label, onClose, children }: OmniboxDialogProps) {
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    previouslyFocused.current = document.activeElement;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;

      // Ognisko wraca tam, skąd otwarto okno — bez tego po zamknięciu ląduje
      // na `<body>` i nawigacja klawiaturą zaczyna się od początku strony.
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <>
      {/* Przyciemnienie jest klikalne, ale nie jest przyciskiem: zamknięcie kliknięciem
          w tło to skrót, nie jedyna droga — Escape robi to samo i jest w stopce okna.
          Dlatego `aria-hidden`, żeby czytnik ekranu nie ogłaszał pustego elementu. */}
      <div className={styles['overlay']} aria-hidden="true" onClick={onClose} />

      {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- natywny `<dialog>` jest
          modalny dopiero po imperatywnym `showModal()`, którego jsdom nie implementuje.
          Chcemy, żeby testy sprawdzały dokładnie to zachowanie, które widzi przeglądarka,
          więc modalność opisujemy atrybutami. Do rewizji, gdy jsdom to dołoży. */}
      <div className={styles['content']} role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </>,
    document.body,
  );
}
